import { useEffect, useState } from 'react';
import {
  adminListUsers,
  adminCreateUser,
  adminResetPassword,
  adminSetActive,
  ApiError,
  type AdminUser,
} from '../lib/api';
import { Button, Modal, Spinner, ErrorText, EmptyState } from '../components/ui';
import { formatDate } from '../lib/format';

function CreateUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (email: string, tempPassword: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit() {
    if (!email.includes('@')) {
      setError('A valid email is required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await adminCreateUser(email.trim().toLowerCase(), fullName.trim());
      onCreated(r.email, r.tempPassword);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to create user');
      setBusy(false);
    }
  }

  return (
    <Modal title="Add employee" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-slate-600">Email</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">Full name</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5"
          />
        </div>
        <p className="text-xs text-slate-500">
          A temporary password is generated. Share it with the employee; they’ll set their own on first
          sign-in.
        </p>
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
}

export default function AdminPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [notice, setNotice] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState('');

  async function load() {
    try {
      setUsers(await adminListUsers());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load users');
      setUsers([]);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function resetPassword(u: AdminUser) {
    if (!window.confirm(`Reset password for ${u.email}?`)) return;
    try {
      const r = await adminResetPassword(u.id);
      setNotice({ email: u.email, password: r.tempPassword });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Reset failed');
    }
  }

  async function toggleActive(u: AdminUser) {
    try {
      await adminSetActive(u.id, !u.is_active);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Update failed');
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Team</h1>
        <Button onClick={() => setShowCreate(true)}>＋ Add employee</Button>
      </div>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      {notice && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-medium text-amber-800">Temporary password for {notice.email}</p>
          <p className="mt-1 font-mono text-amber-900">{notice.password}</p>
          <p className="mt-1 text-xs text-amber-700">
            Copy it now — it won’t be shown again. Share it securely with the employee.
          </p>
          <button onClick={() => setNotice(null)} className="mt-2 text-xs text-amber-700 underline">
            Dismiss
          </button>
        </div>
      )}

      {users === null ? (
        <Spinner label="Loading…" />
      ) : users.length === 0 ? (
        <EmptyState title="No users yet" />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Added</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2">{u.full_name || '—'}</td>
                  <td className="px-3 py-2">{u.role}</td>
                  <td className="px-3 py-2">
                    <span className={u.is_active ? 'text-green-600' : 'text-slate-400'}>
                      {u.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500">{formatDate(u.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" onClick={() => resetPassword(u)}>
                        Reset password
                      </Button>
                      {u.role !== 'admin' && (
                        <Button variant="ghost" onClick={() => toggleActive(u)}>
                          {u.is_active ? 'Disable' : 'Enable'}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateUserDialog
          onClose={() => setShowCreate(false)}
          onCreated={(email, password) => setNotice({ email, password })}
        />
      )}
    </div>
  );
}
